// Яндекс Метрика ID
export const YANDEX_METRIKA_ID = 106309495;

// Типы для Яндекс Метрики
declare global {
  interface Window {
    ym?: (
      counterId: number,
      method: string,
      ...args: any[]
    ) => void;
  }
}

/**
 * Получение UTM-параметров из URL
 * @returns Объект с UTM-метками
 */
export const getUTMParams = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  // Стандартные UTM-метки
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
};

/**
 * Отправка UTM-параметров в Яндекс.Метрику
 * UTM-метки передаются как параметры визита
 */
export const trackUTMParams = () => {
  if (typeof window === 'undefined' || !window.ym) {
    return;
  }

  const utmParams = getUTMParams();
  
  // Если есть UTM-параметры, отправляем их в Яндекс.Метрику
  if (Object.keys(utmParams).length > 0) {
    // Передаем все UTM-параметры одним объектом
    // Яндекс.Метрика автоматически распознает стандартные UTM-метки (utm_source, utm_medium, и т.д.)
    window.ym(YANDEX_METRIKA_ID, 'params', utmParams);
    
    // Для отладки
    if (process.env.NODE_ENV === 'development') {
      console.log('UTM params sent to Yandex.Metrika:', utmParams);
      console.log('Current URL:', window.location.href);
    }
  } else if (process.env.NODE_ENV === 'development') {
    console.log('No UTM params found in URL:', window.location.href);
  }
};

/**
 * Инициализация Яндекс Метрики
 * Примечание: Метрика уже инициализирована в index.html
 * Эта функция используется только для отслеживания UTM-меток
 */
export const initYandexMetrika = () => {
  // Метрика уже инициализирована в index.html
  // Отслеживаем UTM-метки после загрузки
  if (typeof window !== 'undefined') {
    const checkYm = () => {
      if (window.ym) {
        // Отслеживаем UTM-метки с задержкой, чтобы Метрика успела загрузиться
        setTimeout(() => {
          trackUTMParams();
        }, 300);
      } else {
        // Если Метрика еще не загружена, ждем еще
        setTimeout(checkYm, 100);
      }
    };
    
    checkYm();
  }
};

/**
 * Отслеживание достижения цели
 * @param target - Название цели
 * @param params - Дополнительные параметры
 */
export const reachGoal = (target: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.ym) {
    return;
  }

  window.ym(YANDEX_METRIKA_ID, 'reachGoal', target, params);
};

/**
 * Отслеживание события
 * @param event - Название события
 * @param params - Параметры события
 */
export const trackEvent = (event: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.ym) {
    return;
  }

  window.ym(YANDEX_METRIKA_ID, 'params', params);
};

/**
 * Отслеживание внешних ссылок
 * @param url - URL ссылки
 * @param options - Опции отслеживания
 */
export const trackExternalLink = (
  url: string,
  options?: { callback?: () => void; params?: Record<string, any> }
) => {
  if (typeof window === 'undefined' || !window.ym) {
    if (options?.callback) {
      options.callback();
    }
    return;
  }

  window.ym(YANDEX_METRIKA_ID, 'extLink', url, options);
};

/**
 * Отслеживание файлов
 * @param url - URL файла
 * @param options - Опции отслеживания
 */
export const trackFile = (
  url: string,
  options?: { callback?: () => void; params?: Record<string, any> }
) => {
  if (typeof window === 'undefined' || !window.ym) {
    if (options?.callback) {
      options.callback();
    }
    return;
  }

  window.ym(YANDEX_METRIKA_ID, 'file', url, options);
};

/**
 * Отслеживание клика по кнопке/элементу
 * @param elementName - Название элемента
 * @param params - Дополнительные параметры
 */
export const trackClick = (elementName: string, params?: Record<string, any>) => {
  reachGoal(`click_${elementName}`, params);
};
